import * as React from 'react'
import { tenantModeSchema, type TenantMode } from '@lib/enums/tenant-mode.enum'
import type { Uuid } from '@lib/validators/params'

function storageKey(tenantId: Uuid): string {
  return `pixelsign.dashboardMode.${tenantId}`
}

function readMode(tenantId: Uuid | null): TenantMode {
  if (!tenantId) return 'live'
  try {
    const parsed = tenantModeSchema.safeParse(window.localStorage.getItem(storageKey(tenantId)))
    return parsed.success ? parsed.data : 'live'
  } catch {
    return 'live'
  }
}

/**
 * The dashboard's Live/Test choice for one organization, remembered per
 * browser (a convenience: the API scopes every request regardless). Starts in
 * live when nothing is remembered or storage is unavailable.
 *
 * The mode is tied to the organization it was read for, so the first render
 * after switching organizations already uses the new one's mode (never the
 * previous organization's).
 */
export function useDashboardMode(tenantId: Uuid | null): [TenantMode, (mode: TenantMode) => void] {
  const [state, setState] = React.useState(() => ({ tenantId, mode: readMode(tenantId) }))
  const mode = state.tenantId === tenantId ? state.mode : readMode(tenantId)
  if (state.tenantId !== tenantId) {
    setState({ tenantId, mode })
  }

  const setMode = React.useCallback(
    (next: TenantMode) => {
      setState({ tenantId, mode: next })
      if (!tenantId) return
      try {
        window.localStorage.setItem(storageKey(tenantId), next)
      } catch {
        // Private windows and blocked storage: the choice just isn't remembered.
      }
    },
    [tenantId],
  )

  return [mode, setMode]
}
